import React, { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form/dist/index.ie11';
import {
  Button,
  Container,
  Copy,
  Heading,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import { useHistory } from 'react-router-dom';
import { SignupStepFooter } from 'src/components/common/features/Signup/components/SignupStepFooter/SignupStepFooter';
import { ConfirmationStepCard } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationStepCard/ConfirmationStepCard';
import {
  currencyFormatter,
  transformPenniesToDollar,
} from 'src/utilities/currency-utils';
import {
  getSignupSteps,
  SignupStep,
  useSignupStatus,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { ISignupStepProps } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTrackerStep/SignupProgressTrackerStep';
import { sdk } from 'src/utilities/sdk';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { AnalyticsEventLabel } from 'src/types/Analytics';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { TermsAndConditionsControl } from 'src/components/common/TermsAndConditionsControl/TermsAndConditionsControl';
import { useMealCreditEntityName } from 'src/components/selfService/common/hooks/useMealCreditEntityName';
import { capitalize } from 'src/utilities/string-utils';

export function ConfirmationStep({
  updateMobileStepSize,
}: ISignupStepProps): JSX.Element {
  const { MEAL_CREDIT_ENTITY_NAME: entityName } = useMealCreditEntityName();
  const [signupSteps] = useState(
    // TODO(PLEX-8142): Include VALIDATION once ValidationSummaryCard is implemented.
    getSignupSteps(true, capitalize(entityName.singular)).filter(
      (step) =>
        step.id !== SignupStep.SUMMARY && step.id !== SignupStep.VALIDATION
    )
  );
  const { corpId, isWelcomeBackModalPresent, isAdminIncludedInBudget } =
    useSignupStatus();
  const [budgetAmountWithFeeAdded, setBudgetAmountWithFeeAdded] =
    useState(null);
  const [numberOfPeople, setNumberOfPeople] = useState(null);
  const { status: submissionStatus, startAsyncTask } = useAsyncTask();
  const isSubmitting = submissionStatus === AsyncTaskStatus.IN_PROGRESS;
  const isSubmissionCompleted = submissionStatus === AsyncTaskStatus.COMPLETE;
  const isSubmissionError = submissionStatus === AsyncTaskStatus.ERROR;
  const canDetermineEstimate =
    numberOfPeople !== null && budgetAmountWithFeeAdded !== null;

  const useFormProps = useForm({
    mode: 'all',
    defaultValues: {
      termsAndConditionsChecked: false,
    },
  });

  const {
    formState: { isValid },
    handleSubmit,
  } = useFormProps;

  const { push } = useHistory();

  const { sendTealiumPageView, sendTealiumEvent } = useAnalytics();

  useEffect(() => {
    if (!isWelcomeBackModalPresent) {
      // send analytics
      sendTealiumPageView({
        pageGroup: 'corporate self service - sign up flow',
        pageName: 'review',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWelcomeBackModalPresent]);

  useEffect(() => {
    if (isSubmissionCompleted) {
      const baseManagementUrl = `/corporate/management/${corpId}`;
      if (numberOfPeople > 0) {
        // go to account page with modal
        push(`${baseManagementUrl}/account?firstTime=true`);
      } else {
        // go to people page with modal
        push(`${baseManagementUrl}/people?firstTime=true`);
      }

      sendTealiumEvent({
        eventcategory: 'self-service sign up_review',
        eventaction: 'submit_cta',
        eventlabel: AnalyticsEventLabel.SUCCESS,
      });
    }

    if (isSubmissionError) {
      sendTealiumEvent({
        eventcategory: 'self-service sign up_review',
        eventaction: 'submit_cta',
        eventlabel: AnalyticsEventLabel.ERROR,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmissionCompleted, isSubmissionError]);

  async function submitCorp() {
    try {
      await startAsyncTask(() => {
        return sdk.http.post(`/corp-admin/corp/self/${corpId}/signup`, {});
      });
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  function onDataLoaded() {
    if (updateMobileStepSize) {
      updateMobileStepSize();
    }
  }

  function getEstimatedTotal(): string {
    if (!canDetermineEstimate) {
      return '';
    }

    if (numberOfPeople > 0) {
      const totalBudgetInPennies = numberOfPeople * budgetAmountWithFeeAdded;
      const totalBudgetInDollars =
        transformPenniesToDollar(totalBudgetInPennies);

      return currencyFormatter(Number(totalBudgetInDollars));
    }

    const budgetPerPerson = transformPenniesToDollar(budgetAmountWithFeeAdded);
    return currencyFormatter(Number(budgetPerPerson));
  }

  function getAdminIncludedInBudgetCopy(): string {
    return isAdminIncludedInBudget
      ? 'The admin is not included in the estimated total amount. '
      : '';
  }

  return (
    <FormProvider {...useFormProps}>
      <form onSubmit={handleSubmit(submitCorp)}>
        <Container variant="sm">
          <Stack y="sm">
            <Heading variant="h1">Review and submit</Heading>
          </Stack>
          {signupSteps.map(({ id }, index) => {
            const isLastStep = index === signupSteps.length - 1;

            return (
              <Stack key={id} y={isLastStep ? 'lg' : 'sm'}>
                <ConfirmationStepCard
                  stepId={id}
                  onDataLoaded={onDataLoaded}
                  setBudgetAmountWithFeeAdded={setBudgetAmountWithFeeAdded}
                  setNumberOfPeople={setNumberOfPeople}
                />
              </Stack>
            );
          })}
          {canDetermineEstimate && (
            <>
              <Stack y="md">
                <Heading variant="h5">
                  Estimated total: {getEstimatedTotal()}
                  {numberOfPeople === 0 && (
                    <Copy
                      variant="body"
                      color="secondaryText"
                      fontFamily="default"
                    >
                      {' '}
                      / person
                    </Copy>
                  )}
                </Heading>
                <Copy variant="body">
                  Estimated total based on your credit and the number of people
                  you're feeding. {getAdminIncludedInBudgetCopy()}
                  As a reminder,{' '}
                  <Copy variant="body" fontFamily="secondary">
                    you will not be charged
                  </Copy>{' '}
                  until someone places an order.
                </Copy>
              </Stack>
            </>
          )}
          <TermsAndConditionsControl
            name="termsAndConditionsChecked"
            id="termsAndConditionsChecked"
            rules={{ required: true }}
          />
        </Container>
        <SignupStepFooter>
          <Button
            variant="primary"
            type="submit"
            block
            buttonSize="medium"
            disabled={isSubmitting || isSubmissionCompleted || !isValid}
            isLoading={isSubmitting}
            onClick={handleSubmit(submitCorp)}
          >
            Submit
          </Button>
        </SignupStepFooter>
      </form>
    </FormProvider>
  );
}
